// Authentifizierte /api-Aufrufe mit frischem Token.
//
// Hintergrund (Bug „Ungültiger Token" beim Speichern, 2026-10-13): Das
// Supabase-SDK erneuert den Access-Token nur, solange der Tab sichtbar
// ist. Bei gesperrtem PC oder Tab im Hintergrund ruht der Refresh, und
// der Token im authStore läuft nach einer Stunde ab. Wer direkt nach
// dem Entsperren speichert, schickt den abgelaufenen Token und bekommt
// einen 401. Deshalb holen wir den Token hier immer über
// supabase.auth.getSession() (erneuert selbst, wenn er in < 90 s
// abläuft) und versuchen es nach einem 401 genau einmal mit
// refreshSession() erneut.
//
// Nie aus dem onAuthStateChange-Listener heraus awaiten: auth-js 2.103
// ruft Listener mit gehaltener Sperre auf; ein getSession()/refresh-
// Session() von dort wartet auf dieselbe Sperre (pendingInLock) und
// hängt für immer. Siehe authStore.listener.test.js.
//
// 401 mit `reason` (session_replaced / session_not_registered /
// session_expired) sind
// Device-Limit-Entscheidungen des Servers. Die wiederholen wir nicht,
// darum kümmert sich der Fetch-Interceptor in apiClient.js.

import * as Sentry from '@sentry/react';
import { supabase } from './supabase';
import { isSentryActive } from './sentry';
import { useAuthStore } from '../stores/authStore';

// getSession() liefert bei jedem Aufruf ein neues Objekt. Verglichen
// wird deshalb der Token, sonst würden Komponenten, die von `session`
// abhängen (z.B. Admin-Listen), bei jedem Aufruf neu laden.
// Ist der Store leer, läuft gerade ein Logout (signOut leert den Store
// vor dem SDK); dann nichts zurückschreiben.
// Obergrenze für getSession()/refreshSession(). Hängt die Supabase-
// Bibliothek (z.B. blockierte Sperre, siehe authStore-Listener), soll das
// Speichern sichtbar scheitern statt ewig zu warten.
const AUTH_TIMEOUT_MS = 8000;

function withTimeout(promise, ms, label) {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(`${label} antwortet nicht`)), ms);
    })
  ]).finally(() => clearTimeout(timer));
}

function syncStore(session) {
  const current = useAuthStore.getState().session;
  if (current && session?.access_token && session.access_token !== current.access_token) {
    useAuthStore.setState({ session, user: session.user || null });
  }
}

/**
 * Liefert einen gültigen Access-Token oder null, wenn niemand
 * angemeldet ist. Hält den authStore auf dem Stand des SDK.
 */
export async function getAccessToken() {
  try {
    const { data: { session } } = await withTimeout(supabase.auth.getSession(), AUTH_TIMEOUT_MS, 'getSession');
    if (session?.access_token) syncStore(session);
    return session?.access_token ?? null;
  } catch (err) {
    console.warn('[authFetch] getSession failed:', err?.message);
    return useAuthStore.getState().session?.access_token ?? null;
  }
}

async function readReason(response) {
  try {
    const body = await response.clone().json();
    return body?.reason ?? null;
  } catch {
    return null;
  }
}

// Nur die Route ohne IDs, damit das Event keine Projekt- oder
// Berechnungs-IDs enthält.
function routeOf(input) {
  const url = typeof input === 'string' ? input : input?.url || '';
  return url
    .replace(/^https?:\/\/[^/]+/, '')
    .split('?')[0]
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':id');
}

// Meldung des Refresh-Fehlers für Sentry, ohne Tokens.
function scrubMessage(msg) {
  return String(msg || '')
    .replace(/Bearer\s+\S+/gi, 'Bearer :token')
    .replace(/eyJ[\w-]+\.[\w-]+\.[\w-]*/g, ':jwt')
    .slice(0, 120);
}

// Typ des gescheiterten Refreshs, damit Netz (TypeError „Failed to
// fetch“, AuthRetryableFetchError), Sperre (AbortError), Zeitgrenze und
// abgelehnter Refresh-Token (AuthApiError) unterscheidbar sind.
function describeRefreshFailure(error) {
  if (!error) return { refresh_error: 'NoSession' };
  const timeout = /antwortet nicht$/.test(error.message || '');
  return {
    refresh_error: timeout ? 'Timeout' : (error.name || 'Error'),
    refresh_message: scrubMessage(error.message)
  };
}

function reportAuthFailure(stage, input, method, details = {}) {
  if (!isSentryActive()) return;
  Sentry.captureMessage('API 401 trotz Token-Refresh', {
    level: 'warning',
    tags: { auth_stage: stage },
    extra: { route: routeOf(input), method: method || 'GET', ...details }
  });
}

/**
 * fetch() für /api-Routen. Setzt den Bearer-Token, wenn der Aufrufer
 * keinen mitgibt, und wiederholt einen 401 ohne `reason` einmal mit
 * erneuertem Token. Gibt immer die letzte Response zurück; Fehler-
 * behandlung bleibt beim Aufrufer.
 */
export async function authFetch(input, init = {}) {
  const headers = new Headers(init.headers || {});
  if (!headers.has('Authorization')) {
    const token = await getAccessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(input, { ...init, headers });
  if (response.status !== 401) return response;
  if (await readReason(response)) return response;

  let refreshed = null;
  let refreshError = null;
  try {
    const { data, error } = await withTimeout(supabase.auth.refreshSession(), AUTH_TIMEOUT_MS, 'refreshSession');
    if (error) refreshError = error;
    else refreshed = data?.session ?? null;
  } catch (err) {
    refreshError = err;
    console.warn('[authFetch] refreshSession failed:', err?.message);
  }
  if (!refreshed?.access_token) {
    reportAuthFailure('refresh', input, init.method, describeRefreshFailure(refreshError));
    return response;
  }
  syncStore(refreshed);

  const retryHeaders = new Headers(headers);
  retryHeaders.set('Authorization', `Bearer ${refreshed.access_token}`);
  const retry = await fetch(input, { ...init, headers: retryHeaders });
  if (retry.status === 401 && !(await readReason(retry))) {
    reportAuthFailure('retry', input, init.method);
  }
  return retry;
}
